import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Crown } from "lucide-react";
import { Avatar } from "@/components/Avatar";

const BANDS = [
    { id: "grandparent", label: "Grandparents", relationships: ["grandparent"] },
    { id: "parent_sibling", label: "Parents & Siblings", relationships: ["parent", "sibling"] },
    { id: "child", label: "Kids", relationships: ["child"] },
    { id: "other", label: "Family & Loved Ones", relationships: ["other"] },
];

export function FamilyTree({ members, currentUserId }) {
    const containerRef = useRef(null);
    const nodeRefs = useRef({});
    const [links, setLinks] = useState([]);

    const measure = useCallback(() => {
        const container = containerRef.current;
        if (!container) return;
        const cRect = container.getBoundingClientRect();
        const out = [];
        members.forEach((m) => {
            (m.parent_ids || []).forEach((pid) => {
                const childEl = nodeRefs.current[m.user_id];
                const parentEl = nodeRefs.current[pid];
                if (!childEl || !parentEl) return;
                const cr = childEl.getBoundingClientRect();
                const pr = parentEl.getBoundingClientRect();
                out.push({
                    key: `${pid}-${m.user_id}`,
                    x1: pr.left + pr.width / 2 - cRect.left,
                    y1: pr.bottom - cRect.top - 6,
                    x2: cr.left + cr.width / 2 - cRect.left,
                    y2: cr.top - cRect.top + 6,
                });
            });
        });
        setLinks(out);
    }, [members]);

    useLayoutEffect(() => {
        const t = setTimeout(measure, 550);
        const onResize = () => measure();
        window.addEventListener("resize", onResize);
        return () => {
            clearTimeout(t);
            window.removeEventListener("resize", onResize);
        };
    }, [measure]);

    const bands = BANDS.map((b) => ({
        ...b,
        members: members.filter((m) => b.relationships.includes(m.relationship || "other")),
    })).filter((b) => b.members.length > 0);

    if (!bands.length) return null;

    return (
        <div data-testid="family-tree" className="relative mt-8" ref={containerRef}>
            <svg className="pointer-events-none absolute inset-0 z-10 h-full w-full" aria-hidden="true">
                {links.map((l) => {
                    const my = (l.y1 + l.y2) / 2;
                    return (
                        <path
                            key={l.key}
                            data-testid={`tree-link-${l.key}`}
                            d={`M${l.x1},${l.y1} C${l.x1},${my} ${l.x2},${my} ${l.x2},${l.y2}`}
                            fill="none"
                            stroke="#8E7BB5"
                            strokeWidth="2"
                            strokeLinecap="round"
                            opacity="0.85"
                        />
                    );
                })}
            </svg>
            {bands.map((band, idx) => (
                <div key={band.id}>
                    {idx > 0 && (
                        <div className="mx-auto h-10 w-0.5 bg-gradient-to-b from-lavender to-peach" aria-hidden="true" />
                    )}
                    <motion.div
                        data-testid={`tree-band-${band.id}`}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: idx * 0.1, ease: [0.76, 0, 0.24, 1] }}
                        className="rounded-3xl border-2 border-warmgray bg-ivory p-6"
                    >
                        <p className="text-center font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-[#C98A94]">
                            {band.label}
                        </p>
                        <div className="mt-5 flex flex-wrap items-start justify-center gap-6">
                            {band.members.map((m, i) => (
                                <motion.div
                                    key={m.user_id}
                                    ref={(el) => {
                                        if (el) nodeRefs.current[m.user_id] = el;
                                    }}
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.4, delay: idx * 0.1 + i * 0.05 }}
                                >
                                    <Link
                                        to={`/dashboard?author=${m.user_id}`}
                                        data-testid={`tree-node-${m.user_id}`}
                                        className="group flex w-28 flex-col items-center text-center"
                                    >
                                        <div
                                            className={`relative rounded-full p-1 transition-all duration-300 group-hover:scale-105 ${
                                                m.user_id === currentUserId
                                                    ? "bg-gradient-to-br from-lavender to-sunset"
                                                    : "group-hover:bg-warmgray"
                                            }`}
                                        >
                                            <Avatar person={m} size={64} />
                                            {m.is_owner && (
                                                <span
                                                    data-testid={`tree-owner-badge-${m.user_id}`}
                                                    className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full bg-navy text-white"
                                                    title="Family owner"
                                                >
                                                    <Crown size={11} />
                                                </span>
                                            )}
                                        </div>
                                        <p className="mt-2 w-full truncate text-xs font-semibold text-navy">
                                            {m.name || m.email}
                                            {m.user_id === currentUserId && <span className="text-[#C98A94]"> · You</span>}
                                        </p>
                                        <p className="font-mono text-[9px] uppercase tracking-widest text-navy/50">
                                            {m.memory_count} {m.memory_count === 1 ? "memory" : "memories"}
                                        </p>
                                    </Link>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                </div>
            ))}
        </div>
    );
}
